import React, {useMemo, useState} from 'react';
import {Alert, FlexColumn, FlexRow, Modal, RadioGroupHandler, SubHeading, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ExpenseBoolean, ExpenseRow} from '../ExpenseTypes';
import BulkUpdateExpenseItemsMutation from '../../../mutations/bulk_update_expense_items_mutation';
import Util from '../../../forecast-app/shared/util/util';

type ChangeExpenseBillableStatusModalProps = {
	expenseItems: ExpenseRow[];
	closeModal: () => void;
};

const ChangeExpenseBillableStatusModal = ({
	expenseItems,
	closeModal,
}: ChangeExpenseBillableStatusModalProps): React.ReactElement => {
	const {formatMessage} = useIntl();
	const updateableItems = useMemo(() => expenseItems.filter(expenseItem => !expenseItem.locked), [expenseItems]);

	const [newBillableStatus, setNewBillableStatus] = useState<string>('');

	const count = expenseItems.length;
	const lockedCount = expenseItems.length - updateableItems.length;
	const showAlert = lockedCount > 0;
	const allowSave = newBillableStatus && count > lockedCount;

	const saveChanges = () => {
		const onSuccess = () => {
			closeModal();
		};
		Util.CommitMutation(
			BulkUpdateExpenseItemsMutation,
			{
				ids: updateableItems.map(expenseItem => expenseItem.id),
				billable: newBillableStatus === ExpenseBoolean.TRUE,
			},
			onSuccess
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="expense_management.mark_as_billable" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'s'}>
					<FlexRow>
						<SubHeading>
							<FormattedMessage id={'expense_management.billing_details'} />
						</SubHeading>
					</FlexRow>
					<FlexRow>
						<Text color={'medium'}>
							<FormattedMessage id={'expense_management.set_billing_option'} />
						</Text>
					</FlexRow>
				</FlexColumn>
				<FlexColumn gap={'xl'}>
					<RadioGroupHandler onValueChange={setNewBillableStatus} value={newBillableStatus}>
						<FlexRow gap={'s'}>
							<RadioGroupHandler.Item value={ExpenseBoolean.TRUE} />
							<FlexColumn>
								<Text>{formatMessage({id: 'expense_management.change_billable_status.option_1'})}</Text>
								<Text color={'medium'}>
									{formatMessage({id: 'expense_management.change_billable_status.option_1_subheading'})}
								</Text>
							</FlexColumn>
						</FlexRow>
						<FlexRow gap={'s'}>
							<RadioGroupHandler.Item value={ExpenseBoolean.FALSE} />
							<Text>{formatMessage({id: 'expense_management.change_billable_status.option_2'})}</Text>
						</FlexRow>
					</RadioGroupHandler>
					{showAlert && (
						<Alert
							text={formatMessage(
								{id: 'expense_management.change_billable_status.locked_alert'},
								{count, lockedCount}
							)}
							type={Alert.TYPE.INFORMATION}
						/>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={saveChanges} disabled={!allowSave} dataCy={'confirm'}>
					<FormattedMessage id={'expense_management.actions.confirm_changes'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy={'cancel'}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ChangeExpenseBillableStatusModal;
